import { autoinject, bindingMode, computedFrom } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { CwqiManager } from 'app/ext/cwqi/cwqi-manager';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { guid } from 'app/shared/lib/guid';
import { AuthContext } from 'app/um/lib/auth-context';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthReloadEvent } from 'app/um/lib/events';
import { AuthService } from 'app/um/services/auth-service';
import { EssDialogCustomElement } from 'app/shared/elements/ess-dialog';
import { ICustomFieldTargetColumnsContainer, ICustomFieldTargetColumnsSettings } from 'app/shared/lib/custom-field-definitions';
import { MemberType } from 'app/cd/services/member-service';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { debounce } from 'app/shared/lib/decorators';
import { IqcSummary } from 'app/ext/iqc/iqc-summary';
import * as UM from 'app/um/models/essette-api';

export interface IAuthInformationOptions {
    showCWQIAction: boolean;
    showIQCAction: boolean;
    showAssignedTo: boolean;
    showDueDate: boolean;
    showHealthcareServiceType: boolean;
    showPlaceOfService: boolean;
}

const defaultOptions: IAuthInformationOptions = {
    showCWQIAction: true,
    showIQCAction: false,
    showAssignedTo: true,
    showDueDate: true,
    showHealthcareServiceType: false,
    showPlaceOfService: false

}

@autoinject()
export class AuthInformation extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public authId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public options: IAuthInformationOptions = defaultOptions;

    protected auth: UM.Auth | null = null;

    @bindable.string({ defaultBindingMode: bindingMode.toView })
    protected memberType: MemberType = MemberType.Member;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    protected id: guid = guid();

    protected iqcSummaryModalOpened = false;

    private authReloaded: Subscription | null = null;

    @computedFrom('auth.flag.potentialQualityIssue', 'auth.flag.potentialThirdPartyLiability', 'auth.flag.potentialCaseManagement')
    private get flagCount(): number {
        var count = 0;

        if(this.auth?.flag?.potentialQualityIssue) count++;
        if(this.auth?.flag?.potentialThirdPartyLiability) count++;
        if(this.auth?.flag?.potentialCaseManagement) count++;

        return count;
    }

    ////****************** Element References ***************************************////
    protected $iqcModal!: EssDialogCustomElement;
    protected $iqcSummary!: IqcSummary;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: AuthContext,
        private readonly customFieldContext: CustomFieldContext,
        private readonly events: EventAggregator,
        private readonly authService: AuthService,
        protected cwqi: CwqiManager,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(this.memberType === MemberType.Member) this.loadCustomFieldTargets();
        this.reload();

        this.authReloaded = this.events.subscribe(AuthReloadEvent, (e: AuthReloadEvent) => {
            this.reload();
        });
    }

    protected detached(): void {
        if(this.authReloaded) {
            this.authReloaded.dispose();
        }
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        try {
            if(this.context.auth.id && this.context.auth.id === this.authId) {
                this.auth = this.context.auth;
                if(!this.auth.priority || !this.auth.priority.description) {
                    await this.loadAuth();
                }
            } else {
                await this.loadAuth();
            }
        } catch(error) {
            throw error;
        }
    }

    ////****************** Functions ************************************************////
    protected toggleIqcSummaryModal(): void {
        this.iqcSummaryModalOpened = true;
        this.openIqcSummaryModal();
    }

    @debounce(0)
    protected openIqcSummaryModal(): void {
        if(!this.$iqcModal) return;
        this.$iqcModal.open();
        this.$iqcSummary.expandAll();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCustomFieldTargets(): Promise<void> {
        const settings: ICustomFieldTargetColumnsSettings = {
            definitionCount: 10,
            filterInactive: true,
            sortByLabel: true
        };
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('UM.Auth', settings);
    }

    @apiCall
    private async loadAuth(): Promise<void> {
        if(!this.authId) return;

        const includes = [
            'assignedTo',
            'class',
            'class.subClass',
            'externalSystem',
            'healthcareServiceType',
            'latestEagleTransaction',
            'member',
            'member.plan',
            'member.plan.lineOfBusiness',
            'metadata.requestedOverride.byUser',
            'metadata.requestedOverride.reason',
            'placeOfService',
            'priority',
            'serviceDate',
            'services.detail.authmedicalreview',
            'status',
            'type'
        ];

        const fields = [
            'admission.date',
            'alternateNumber',
            'assignedTo.description',
            'class.description',
            'class.subClass.description',
            'discharge.date',
            'dueDateTime',
            'externalSystem.*',
            'flag.*',
            'healthcareServiceType.description',
            'isAdmission',
            'latestEagleTransaction.metadata.created.timestamp',
            'latestEagleTransaction.flag.isComplete',
            'member.plan.lineOfBusiness.description',
            'metadata.received.timestamp',
            'metadata.requestedOverride.byUser.description',
            'metadata.requestedOverride.reason.description',
            'metadata.requestedOverride.timestamp',
            'number',
            'placeOfService.description',
            'priority.description',
            'requestedDateTime',
            'serviceDate.dateRange.end',
            'serviceDate.dateRange.start',
            'status.description',
            'type.description'
        ];

        // do not include UDF data in portal
        if(this.memberType === MemberType.Member) {
            includes.push('custom');
            fields.push('custom.text.*', 'custom.lookup.*');
        }

        try {
            this.state.beginLoad();

            const response = await this.authService.loadAuth(this.authId, includes, {}, fields);
            this.auth = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
